import React, { lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, XIcon, CheckCircleIcon, GiftIcon, ShieldCheckIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { TestimonialSection } from './TestimonialSection';
const CHECKOUT_URL = 'https://checkout.ecompradigital.online/cart/45913855623356:1';
const handleImageClick = () => {
  window.open(CHECKOUT_URL, '_blank');
};
const painItems = [{
  title: 'Armar el programa anual',
  sub: 'Horas frente a la computadora sin avanzar'
}, {
  title: 'Preparar evaluaciones desde cero',
  sub: 'Reinventando la rueda cada ciclo'
}, {
  title: 'Buscar casos prácticos nuevos',
  sub: 'Navegando internet sin encontrar material de calidad'
}, {
  title: 'Organizar planificaciones y documentación',
  sub: 'Papeles, carpetas, archivos... un caos'
}];
const solutionItems = ['Programa anual editable listo para usar', 'Clases estructuradas desde el primer día', 'Casos profesionales reales preparados para trabajar', 'Evaluaciones completas con criterios claros', 'Documentación docente lista para presentar', 'Toda tu planificación organizada en un solo sistema'];
const fadeInView = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-50px'
  },
  transition: {
    duration: 0.5
  }
};
export function SalesPage() {
  return <div className="w-full bg-white" data-id="element-16">
      {/* Spacer for fixed header */}
      <div className="h-10" data-id="element-17" />

      {/* SECTION 2 - HERO (dark bg) */}
      <section className="relative py-4 md:py-5 px-4 bg-gray-900 overflow-hidden" data-id="element-18">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1200&q=60')"
      }} data-id="element-19" />
        {/* Dark Overlay — no blur for performance */}
        <div className="absolute inset-0 bg-gray-900/85" data-id="element-20" />

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-21">
          <span className="inline-block bg-yellow-500/20 text-yellow-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-1" data-id="element-22">
            🦺 Para docentes de Seguridad e Higiene
          </span>
          <p className="uppercase tracking-wider font-semibold text-[18px] text-[#C8CACD] mt-[10px] mb-[10px]" data-id="element-23">
            MANUAL ACADÉMICO
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-10" data-id="element-24">
            SEGURIDAD E HIGIENE PARA DOCENTES
          </h1>

          {/* Main Product Image */}
          <img src="https://cdn.magicpatterns.com/uploads/vsSCYWcm5xhDx2isBqa9JS/covers_Seguridad_e_Higiene_(1).png" alt="Manual Académico Seguridad e Higiene para Docentes" className="w-full max-w-lg mx-auto -my-6 mb-8" decoding="async" data-id="element-25" />

          <p className="text-xl md:text-2xl text-white font-bold mb-1" data-id="element-26">
            Dejá de perder semanas armando material y empezá el ciclo lectivo
            con todo resuelto desde el primer día.
          </p>
          <p className="text-lg mb-3 text-[#C2C7C9]" data-id="element-27">
            Programas listos, clases estructuradas, casos profesionales
            preparados y evaluaciones organizadas en un solo sistema.
          </p>

          {/* Social Proof - Rating + Avatars */}
          <div className="flex flex-col bg-white/5 border border-white/10 rounded-lg items-center gap-2 mb-3 bg-[#1D2332] max-w-xs mx-auto py-3 px-4" data-id="element-28">
            <div className="flex items-center gap-1" data-id="element-29">
              {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" viewBox="0 0 20 20" data-id="element-30">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-31" />
                </svg>)}
              <span className="text-yellow-300 font-bold text-sm ml-1" data-id="element-32">
                4.9/5
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-33">
              <div className="flex -space-x-2" data-id="element-34">
                {['https://randomuser.me/api/portraits/women/44.jpg', 'https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/women/31.jpg', 'https://randomuser.me/api/portraits/men/55.jpg', 'https://randomuser.me/api/portraits/women/22.jpg'].map((url, i) => <img key={i} src={url} alt="Docente" className="w-7 h-7 rounded-full object-cover border-2 border-gray-900" decoding="async" data-id="element-35" />)}
              </div>
              <span className="text-gray-300 text-sm ml-[40px]" data-id="element-36">
                +500 docentes ya confían en este material
              </span>
            </div>
          </div>

          {/* Micro-testimonial */}
          <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 mb-4 max-w-2xl mx-auto flex items-start gap-3 text-left" data-id="element-37">
            <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="María Fernanda L." className="w-12 h-12 rounded-full object-cover border-2 border-white/20 flex-shrink-0" decoding="async" data-id="element-38" />
            <div data-id="element-39">
              <p className="text-gray-300 text-sm italic" data-id="element-40">
                "Con 15 años de experiencia en el rubro, pensé que no necesitaba
                ayuda. Pero este manual me sorprendió. La organización
                progresiva por año y las rúbricas técnicas son de un nivel
                profesional que no había visto. Lo recomiendo a todos mis
                colegas."
              </p>
              <p className="text-yellow-400 text-xs font-semibold mt-1.5" data-id="element-41">
                — María Fernanda L., Córdoba ⭐⭐⭐⭐⭐
              </p>
            </div>
          </div>

          <CTAButton data-id="element-42">👉 SÍ, QUIERO ACCEDER AHORA</CTAButton>
        </div>
      </section>

      {/* SECTION 3 - PAIN POINT */}
      <section className="relative py-10 px-4 bg-gray-950 overflow-hidden" data-id="element-43">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-900/20 via-gray-950 to-gray-950" data-id="element-44" />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent opacity-50" data-id="element-45" />

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-46">
          <motion.div {...fadeInView} data-id="element-47">
            <h2 className="text-xs md:text-sm font-bold text-red-500 tracking-widest uppercase mb-3" data-id="element-48">
              Si enseñás Seguridad e Higiene, dejame hacerte una pregunta
              honesta
            </h2>
            <p className="text-2xl md:text-4xl text-white font-black mb-6 leading-tight" data-id="element-49">
              ¿Te pasa que el inicio del ciclo lectivo te exige más trabajo
              organizando material que dando clases?
            </p>

            <p className="text-xl md:text-2xl text-gray-300 font-medium mb-6" data-id="element-50">
              Cada año volvés a lo mismo:
            </p>

            {/* Single animated container instead of 4 individual observers */}
            <div className="flex flex-col gap-3 text-left max-w-2xl mx-auto mb-8" data-id="element-51">
              {painItems.map((item, index) => <div key={index} className="flex items-start gap-3" data-id="element-52">
                  <XIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-53" />
                  <div data-id="element-54">
                    <span className="text-base font-bold text-gray-200 mr-2" data-id="element-55">
                      {item.title}:
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-56">{item.sub}</span>
                  </div>
                </div>)}
            </div>

            <p className="text-lg text-gray-400 italic mb-4" data-id="element-57">
              Y cuando finalmente todo está listo… ya perdiste días enteros de
              trabajo.
            </p>

            <div data-id="element-58">
              <p className="text-xl md:text-2xl font-bold text-white mb-1" data-id="element-59">
                El problema no es la falta de conocimiento.
              </p>
              <p className="text-xl md:text-2xl font-black text-red-500" data-id="element-60">
                El problema es no contar con un sistema académico organizado.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 4 - SOLUTION */}
      <section className="relative py-12 px-4 bg-white border-l-4 border-green-500" data-id="element-61">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-emerald-600 opacity-80" data-id="element-62" />
        <div className="max-w-4xl mx-auto text-center" data-id="element-63">
          <motion.div {...fadeInView} data-id="element-64">
            <span className="inline-block bg-green-600 text-white text-sm font-bold px-5 py-2 rounded-full mb-4 shadow-sm" data-id="element-65">
              LA SOLUCIÓN
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4" data-id="element-66">
              Manual Académico Seguridad e Higiene para Docentes
            </h2>
            <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-2xl mx-auto" data-id="element-67">
              Un <strong data-id="element-68">sistema académico completo</strong>, organizado
              progresivamente por año, diseñado para que{' '}
              <strong className="text-green-700" data-id="element-69">
                tu planificación anual quede resuelta en una tarde y tus clases
                queden listas en minutos.
              </strong>
            </p>
          </motion.div>

          <motion.div {...fadeInView} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-id="element-70">
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 flex flex-col items-center text-center" data-id="element-71">
              <div className="w-10 h-10 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-xl mb-3" data-id="element-72">
                1
              </div>
              <p className="text-gray-800 font-semibold" data-id="element-73">Abrís el material</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 flex flex-col items-center text-center" data-id="element-74">
              <div className="w-10 h-10 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-xl mb-3" data-id="element-75">
                2
              </div>
              <p className="text-gray-800 font-semibold" data-id="element-76">
                Elegís el año o el módulo
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 flex flex-col items-center text-center" data-id="element-77">
              <div className="w-10 h-10 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-xl mb-3" data-id="element-78">
                3
              </div>
              <p className="text-gray-800 font-semibold" data-id="element-79">
                Lo aplicás directamente en clase
              </p>
            </div>
          </motion.div>

          <motion.div {...fadeInView} transition={{
          duration: 0.5,
          delay: 0.3
        }} data-id="element-80">
            <p className="text-gray-500 italic mb-6" data-id="element-81">
              Sin improvisar. Sin empezar desde cero. Sin perder semanas
              organizando.
            </p>

            <p className="text-2xl font-bold text-gray-900 mb-6" data-id="element-82">
              Imaginá empezar el año con:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-left max-w-3xl mx-auto mb-10" data-id="element-83">
              {solutionItems.map((text, i) => <div key={i} className="flex items-start gap-3" data-id="element-84">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-85" />
                  <span className="text-gray-700 font-medium" data-id="element-86">{text}</span>
                </div>)}
            </div>

            <blockquote className="border-l-4 border-green-500 bg-green-50/50 p-4 rounded-r-lg text-base max-w-2xl mx-auto text-gray-700 italic" data-id="element-87">
              "Este material fue desarrollado a partir de la experiencia real de
              docentes que dictan Seguridad e Higiene en formación técnica."
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* SECTION - AUTHORITY */}

      {/* BEFORE VS AFTER - Image (moved below pain section) */}

      {/* SECTION 5 - CONTENT BREAKDOWN */}
      <section className="py-10 px-4 bg-gray-50" data-id="element-88">
        <div className="max-w-4xl mx-auto text-center" data-id="element-89">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-90">
            📘 ¿Qué incluye exactamente el Manual Académico?
          </h2>
          <p className="text-xl text-gray-700 font-medium mb-8" data-id="element-91">
            Manual Académico Completo – 1°, 2° y 3° Año
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left mb-8" data-id="element-92">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 h-full flex flex-col" data-id="element-93">
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-94">
                <span className="text-blue-500" data-id="element-95">🔹</span> 1° Año – Fundamentos
              </h3>
              <p className="text-gray-600 text-sm flex-grow" data-id="element-96">
                Normativa, conceptos preventivos y análisis inicial de riesgos.
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 h-full flex flex-col" data-id="element-97">
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-98">
                <span className="text-blue-500" data-id="element-99">🔹</span> 2° Año – Evaluación
                Técnica
              </h3>
              <p className="text-gray-600 text-sm flex-grow" data-id="element-100">
                Cálculo de índices, evaluación cualitativa, ergonomía y análisis
                aplicado.
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 h-full flex flex-col" data-id="element-101">
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-102">
                <span className="text-blue-500" data-id="element-103">🔹</span> 3° Año – Integración
              </h3>
              <p className="text-gray-600 text-sm flex-grow" data-id="element-104">
                Matrices de riesgos, auditorías simuladas, investigación de
                accidentes y proyectos.
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-700 mb-6" data-id="element-105">
            Todo organizado progresivamente y alineado con la práctica
            profesional real.
          </p>

          <CTAButton data-id="element-106">🔒 QUIERO ORGANIZAR MI AÑO ACADÉMICO AHORA</CTAButton>
        </div>
      </section>

      {/* SECTION 7 - BONUSES */}
      <section className="py-6 px-4 bg-white" data-id="element-107">
        <div className="max-w-3xl mx-auto" data-id="element-108">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-1" data-id="element-109">
            🎁 BONOS INCLUIDOS HOY
          </h2>
          <p className="text-sm text-center text-gray-600 mb-3" data-id="element-110">
            (SIN COSTO ADICIONAL)
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-111">
            {/* Bono 1 */}
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 flex flex-col items-center text-center" data-id="element-112">
              <span className="bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full mb-2" data-id="element-113">
                🎉 GRATIS HOY
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/kgoqHxYAM6dMoYb7Yz9w8C/MOCKUP_BONO_1.png" alt="Bono 1 - Sistema de Documentación" className="w-20 h-20 object-contain mb-2" loading="lazy" decoding="async" data-id="element-114" />
              <h3 className="text-sm font-bold text-gray-900 mb-1 leading-tight" data-id="element-115">
                Bono #1 – Documentación Docente Profesional
              </h3>
              <p className="text-xs text-gray-500 mb-1" data-id="element-116">
                Programa anual, planificación clase por clase, rúbricas e
                informes editables.
              </p>
              <span className="text-xs text-gray-400 line-through" data-id="element-117">
                Valor: $24,990
              </span>
            </div>

            {/* Bono 2 */}
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 flex flex-col items-center text-center" data-id="element-118">
              <span className="bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full mb-2" data-id="element-119">
                🎉 GRATIS HOY
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/mhpCqUPp2aePhXAomJ6g9Y/MOCKUP_BONO_2.png" alt="Bono 2 - Banco de Casos Profesionales" className="w-20 h-20 object-contain mb-2" loading="lazy" decoding="async" data-id="element-120" />
              <h3 className="text-sm font-bold text-gray-900 mb-1 leading-tight" data-id="element-121">
                Bono #2 – Banco de Casos Profesionales (+150 Páginas)
              </h3>
              <p className="text-xs text-gray-500 mb-1" data-id="element-122">
                +150 páginas de casos técnicos, auditorías simuladas y
                resoluciones docentes.
              </p>
              <span className="text-xs text-gray-400 line-through" data-id="element-123">
                Valor: $29,990
              </span>
            </div>

            {/* Bono 3 */}
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 flex flex-col items-center text-center" data-id="element-124">
              <span className="bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full mb-2" data-id="element-125">
                🎉 GRATIS HOY
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/xxUSGK9FvftiX83iXGGANn/MOCKUP_BONO_3.png" alt="Bono 3 - Kit de Evaluaciones" className="w-20 h-20 object-contain mb-2" loading="lazy" decoding="async" data-id="element-126" />
              <h3 className="text-sm font-bold text-gray-900 mb-1 leading-tight" data-id="element-127">
                Bono #3 – Kit de Evaluaciones y Checklists
              </h3>
              <p className="text-xs text-gray-500 mb-1" data-id="element-128">
                Exámenes, trabajos prácticos, checklists de inspección y claves
                de corrección.
              </p>
              <span className="text-xs text-gray-400 line-through" data-id="element-129">
                Valor: $24,990
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 - WHAT YOU RECEIVE + PRICING */}
      <section id="compra" className="py-10 px-4 bg-gray-50" data-id="element-130">
        <div className="max-w-3xl mx-auto" data-id="element-131">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-6" data-id="element-132">
            📦 Lo que vas a recibir hoy
          </h2>

          <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-200 mb-6" data-id="element-133">
            <ul className="space-y-3 mb-6" data-id="element-134">
              <li className="flex items-center gap-3 text-lg" data-id="element-135">
                <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-136" />
                <span className="text-gray-800 font-medium" data-id="element-137">
                  Manual Académico 1°, 2° y 3° Año
                </span>
              </li>
              <li className="flex items-center gap-3 text-lg" data-id="element-138">
                <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-139" />
                <span className="text-gray-800 font-medium" data-id="element-140">
                  Bono #1: Sistema de Documentación Docente
                </span>
              </li>
              <li className="flex items-center gap-3 text-lg" data-id="element-141">
                <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-142" />
                <span className="text-gray-800 font-medium" data-id="element-143">
                  Bono #2: Banco de Casos Profesionales
                </span>
              </li>
              <li className="flex items-center gap-3 text-lg" data-id="element-144">
                <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-145" />
                <span className="text-gray-800 font-medium" data-id="element-146">
                  Bono #3: Kit de Evaluaciones Profesionales
                </span>
              </li>
              <li className="flex items-center gap-3 text-lg" data-id="element-147">
                <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-148" />
                <span className="text-gray-800 font-medium" data-id="element-149">
                  Acceso inmediato y de por vida
                </span>
              </li>
            </ul>

            {/* Pricing */}
            <div className="pt-6 border-t border-gray-100 text-center bg-gray-50/50 rounded-xl p-4" data-id="element-150">
              <p className="text-gray-500 text-lg line-through mb-1" data-id="element-151">
                Precio regular: $69,990.00
              </p>
              <p className="text-sm text-green-700 font-bold tracking-wide uppercase mb-2" data-id="element-152">
                🔥 Oferta especial por tiempo limitado
              </p>
              <p className="text-5xl md:text-6xl font-black text-green-600 mb-1" data-id="element-153">
                $19,990<span className="text-2xl" data-id="element-154">.00</span>
              </p>
              <p className="text-gray-600 font-medium text-sm mb-4" data-id="element-155">
                Ahorrás $50,000 comprando hoy
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500 font-medium mb-4" data-id="element-156">
                <span className="flex items-center gap-1" data-id="element-157">
                  <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-158" /> Pago
                  100% Seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-159">
                  <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-160" />{' '}
                  Garantía de 7 días
                </span>
                <span className="flex items-center gap-1" data-id="element-161">
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-162">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" data-id="element-163" />
                  </svg>
                  Acceso inmediato
                </span>
              </div>

              <div className="flex items-center justify-center gap-1.5 text-xs text-blue-600 font-medium mb-4 bg-blue-50 py-1.5 px-3 rounded-full inline-flex mx-auto" data-id="element-164">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" data-id="element-165">
                  <path d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm-.5 5a1 1 0 0 1 1 0l3 5.5a1 1 0 0 1-.87 1.5H8.37a1 1 0 0 1-.87-1.5l3-5.5zM12 17a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" data-id="element-166" />
                </svg>
                Pago protegido por Mercado Pago
              </div>

              <CTAButton mode="checkout" className="mt-2" data-id="element-167">
                👉 SÍ, QUIERO ACCEDER AHORA
              </CTAButton>

              <p className="mt-4 text-sm text-gray-500" data-id="element-168">
                ¿Tenés alguna duda?{' '}
                <a href="https://wa.me/5493813588908" target="_blank" rel="noopener noreferrer" className="text-green-600 font-semibold hover:underline" data-id="element-169">
                  No dudes en enviarnos un WhatsApp!
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 - GUARANTEE */}
      <section className="py-5 px-4 bg-white" data-id="element-170">
        <div className="max-w-2xl mx-auto text-center" data-id="element-171">
          <div className="bg-green-50 rounded-2xl p-5 border-2 border-green-200" data-id="element-172">
            <ShieldCheckIcon className="w-12 h-12 text-green-600 mx-auto mb-2" data-id="element-173" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-174">
              Garantía sin riesgo
            </h2>
            <p className="text-lg text-gray-700" data-id="element-175">
              Probalo durante 7 días. Si no te ayuda a organizar tu año
              académico y reducir tu carga de planificación, te devolvemos el
              100% del dinero.
            </p>
            <p className="text-gray-400 text-xs mt-3 flex items-center justify-center gap-1.5" data-id="element-176">
              🔒 Pago protegido por Mercado Pago
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialSection data-id="element-177" />

      {/* SECTION 11 - FINAL CTA */}
      <section className="py-8 px-4 bg-gray-900" data-id="element-178">
        <div className="max-w-3xl mx-auto text-center" data-id="element-179">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-180">
            Enseñar Seguridad e Higiene puede ser mucho más simple
          </h2>
          <p className="text-xl text-gray-300 mb-4" data-id="element-181">
            No necesitás más material suelto. Necesitás un sistema académico
            completo que sostenga todo el año.
          </p>
          <CTAButton className="mb-2" data-id="element-182">
            👉 Accedé ahora al Manual Académico Seguridad e Higiene para
            Docentes
          </CTAButton>
          <p className="text-gray-400 mt-3" data-id="element-183">
            Y empezá el próximo ciclo lectivo con toda tu planificación resuelta
            desde el primer día.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-4 px-4 bg-gray-950 text-center" data-id="element-184">
        <p className="text-gray-500 text-sm" data-id="element-185">
          © 2026 Manual Académico Seguridad e Higiene. Todos los derechos
          reservados.
        </p>
      </footer>

      {/* WhatsApp Floating Button */}
      <a href="https://wa.me/5493813588908" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 flex items-center gap-2" data-id="element-186">
        <span className="bg-white text-gray-800 text-sm font-medium px-3 py-2 rounded-lg shadow-lg border border-gray-200 hidden md:block" data-id="element-187">
          ¿Tenés dudas? ¡Mandanos un WhatsApp!
        </span>
        <div className="bg-green-500 hover:bg-green-600 transition-colors w-14 h-14 rounded-full flex items-center justify-center shadow-lg" data-id="element-188">
          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-189">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-190" />
          </svg>
        </div>
      </a>
    </div>;
}